import QueryString from "qs";
import { emptyApi } from "./emptyApi";

export const TASK_TYPE = {
  FILINGS: "Filings",
  REGISTRATIONS: "Registrations",
} as const;

export type TaskTrackerType = (typeof TASK_TYPE)[keyof typeof TASK_TYPE];

export const PAYMENT_SCHEDULE = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  ANNUALLY: "ANNUALLY",
  SEMI_ANNUALLY: "SEMI_ANNUALLY",
};

export type PaymentScheduleType =
  (typeof PAYMENT_SCHEDULE)[keyof typeof PAYMENT_SCHEDULE];

type Group = {
  uuid: string;
  name: string;
};
type Entity = {
  uuid: string;
  name: string;
};

type Preparer = {
  profile_id?: string;
  name?: string;
  assigned_on?: string;
};

type ServiceTeam = {
  uuid?: string;
  name?: string;
};

export type Transaction = {
  channel_url?: string;
  uuid: string;
  deadline: string | null;
  entity: Entity;
  group: Group;
  preparer: Preparer;
  request_date: string | null;
  service_team: ServiceTeam;
  state: string;
  status: string;
  task_type: string;
  payment_schedule: PaymentScheduleType;
  start_date: string;
  end_date: string;
  state_id: string;
};

export type RegistrationTask = {
  entity: Entity;
  group: Group;
  uuid: string;
  task_type: string;
  state: string;
  status: string;
  deadline?: string;
  request_date?: string;
  channel_url?: string;
};

export type TransactionType = Transaction & RegistrationTask;

type AssignTaskPayload = {
  service_team_id?: string;
  task_id: string;
  task_type: TaskTrackerType;
  profile_id?: string;
  is_remove_profile_id?: boolean;
};

type TaskTrackerResponse = {
  transactions: TransactionType[];
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
};

type TaskTrackerTrackerFilterType = {
  type: TaskTrackerType;
  state?: string;
  status?: string;
  paymentSchedule?: string;
  page?: number;
  searchTerm?: string;
  unassignedServiceTeam?: boolean;
  unassignedPreparer?: boolean;
  dueFilings?: boolean;
  pending_registrations?: boolean;
  serviceTeamIds?: string;
};

export const taskTrackerSalesTax = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getSalesTaxTask: build.query<
      TaskTrackerResponse,
      TaskTrackerTrackerFilterType
    >({
      query: ({
        type,
        state,
        status,
        paymentSchedule,
        page,
        searchTerm = undefined,
        unassignedServiceTeam,
        unassignedPreparer,
        dueFilings,
        pending_registrations,
        serviceTeamIds,
      }) => {
        let queryUrl = QueryString.stringify(
          {
            state,
            task_type: type,
            task_status: status,
            payment_schedule: paymentSchedule,
            page,
            search_term: searchTerm,
            unassigned_service_team: unassignedServiceTeam,
            unassigned_preparer: unassignedPreparer,
            due_filings: dueFilings,
            pending_registrations,
            service_team_ids: serviceTeamIds,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/salestax/tasks/${queryUrl}`,
        };
      },
      providesTags: ["SALES_TAX_TASK"],
    }),
    assignTask: build.mutation<
      any,
      {
        payload: AssignTaskPayload;
      }
    >({
      query: ({ payload }) => {
        return {
          url: `api/inkle/salestax/tasks/assign/`,
          body: payload,
          method: "PUT",
        };
      },
      invalidatesTags: (result) => (result ? ["SALES_TAX_TASK"] : []),
    }),
  }),
});

export const { useGetSalesTaxTaskQuery, useAssignTaskMutation } =
  taskTrackerSalesTax;
